import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface I18nContextType {
  language: string;
  changeLanguage: (lng: string) => Promise<void>;
  availableLanguages: { code: string; name: string; nativeName: string }[];
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const availableLanguages = [
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
];

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<string>(i18n.language || 'es');

  useEffect(() => {
    // Sincronizar estado local con i18next
    setLanguage(i18n.language);
  }, [i18n.language]);

  const changeLanguage = async (lng: string) => {
    try {
      await i18n.changeLanguage(lng);
      setLanguage(lng);

      // Guardar en localStorage
      localStorage.setItem('i18nextLng', lng);

      // TODO: Guardar preferencia en el perfil del usuario (backend)
      // await updateUserLanguagePreference(lng);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const value: I18nContextType = {
    language,
    changeLanguage,
    availableLanguages,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
